// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// interfaces/types
import type { ReduxMiddleware, ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import {
    PAYMENT_RECEIVED,
    AI_LENDER_PROBABILITY_SUCCESS,
    TRIGGER_AI_REFRESH,
    APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS
} from '../actionTypes/deskingActionTypes';
import { UPDATED_OFFER_PRICE_IMMEDIATE, UPDATED_DOWN_PAYMENT_IMMEDIATE, SELECTED_TERM } from '../actionTypes/offerActionTypes';
import { SWITCH_VEHICLE } from '../actionTypes/openDealActionTypes';
import { INCLUDE_TRADE_INS_CHANGED } from '../actionTypes/tradesActionTypes';
import { TRADE_IN_PAYMENT_INFO_UPDATE } from '../actionTypes/tradeActionTypes';
import { PRODUCT_CHANGES_PAYMENT_CALL } from '../actionTypes/vehicleProtectionActionTypes';
import {
    ADDED_MANUAL_ACCESSORIES,
    ADD_ACCESSORIES_SELECTIONS,
    ADD_CATALOG_ACCESSORIES,
    REMOVE_CATALOG_ACCESSORIES
} from '../actionTypes/accessoriesActionTypes';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';

// utils
import type { StateTree } from '@makemydeal/dr-dash-types';
import { LENDER_CREDIT_SOURCE_SOFT_PULL } from '@makemydeal/dr-dash-types';
import {
    fetchLenderProbabilityData,
    updateQualificationIds,
    fetchVehicleRecommendations,
    showAIRefreshButton,
    triggerAIRefresh
} from '../actions/deskingActionCreators';
import { getCreditScoreSource, getCurrentOfferType, getCreditTierById, getDealerDefaultCreditTier } from '../selectors/offerRedux';
import { getLenderCreditSource } from '../selectors/deskingSelectors';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { formatQualificationIds } from '../utils/lenderProbabiltyUtils';
import { toCreditScoreSource } from '../utils/creditScoreSourceUtils';
import { SET_SOFT_PULL_CONSENT_PROVIDED } from '../actionTypes/offerActionTypes';
import { actions as offerReduxActionTypes, actionCreators as offerReduxActionCreators } from '@makemydeal/dr-offer-redux';
import { SCENARIOS_INGEST_COMPLETE } from '../actionTypes/multiScenarioActionTypes';

/**
 * AI Middleware
 *
 * Handles AI-related fetches for lender probability and vehicle recommendations.
 * Fetches run on init, explicit refresh (TRIGGER_AI_REFRESH), soft-pull consent, scenario ingest,
 * and payment-driven refresh — not on OFFER_SAVE_SUCCESS (save uses stale/refresh UX instead).
 */
export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);

    // Guard clause to handle undefined action or action without type
    if (!action || !action.type) {
        return;
    }

    const state = store.getState();

    switch (action.type) {
        case initActionTypes.INIT_SUCCESS:
        case SET_SOFT_PULL_CONSENT_PROVIDED:
        case TRIGGER_AI_REFRESH: {
            // For consent actions, only proceed if user accepted (payload === true)
            const isConsentAction = action.type === SET_SOFT_PULL_CONSENT_PROVIDED;

            if (isConsentAction && action.payload !== true) {
                break;
            }

            const isInitSuccessAction = action.type === initActionTypes.INIT_SUCCESS;

            // On INIT_SUCCESS, only fetch for primary scope
            const isPrimaryScope = scopedStateSelectors.isCurrentScopePrimary(state as any);
            if (isInitSuccessAction && !isPrimaryScope) {
                break;
            }

            const currentOfferType = getCurrentOfferType(state); // Get the current offer type
            const isFinanceOffer = currentOfferType === FINANCE; // Check if it's a finance offer
            const isLeaseOffer = currentOfferType === LEASE; // Check if it's a lease offer

            if (isFinanceOffer) {
                // Global feature toggles (finance-only)
                const isLenderComparisonMVEnabled = featureToggleSelectors.isLenderComparisonMVEnabled(state);

                const scopeId = scopedStateSelectors.getCurrentScopeId(state as any);
                const isPrimaryScope = scopedStateSelectors.isCurrentScopePrimary(state as any);
                // getCurrentScopeId returns the bare id, so fetch and routing take the same value; a non-primary
                // scope with no id can't be targeted, so skip rather than fall through to a deal-level fetch.
                const scopeArg = isPrimaryScope ? undefined : scopeId;

                // Fetch AI Lender Probability data (drives both the lender slide-out and the affordability card)
                if (isLenderComparisonMVEnabled && (isPrimaryScope || scopeId)) {
                    store.dispatch(fetchLenderProbabilityData(scopeArg, scopeArg));
                }
            }

            const isAIVehicleRecommendationEnabledForLease = featureToggleSelectors.isAIVehicleRecommendationEnabledForLease(state);
            const isAiVehicleRecommendationsChangeVehicleEnabled =
                featureToggleSelectors.isAiVehicleRecommendationsChangeVehicleEnabled(state);
            // Lease has no renderable recommendation surface while AVO-on-Change-Vehicle is on: the dashboard
            // card self-suppresses and the Change Vehicle section is finance-only. Skip the fetch for every
            // trigger rather than storing finance-shaped recs nothing can display.
            const shouldFetchVehicleRecommendationsForLease =
                isLeaseOffer && isAIVehicleRecommendationEnabledForLease && !isAiVehicleRecommendationsChangeVehicleEnabled;

            // Fetch AI Vehicle Recommendations for finance and lease offers
            if (isFinanceOffer || shouldFetchVehicleRecommendationsForLease) {
                const isAIVehicleRecommendationEnabled = featureToggleSelectors.isAIVehicleRecommendationEnabled(state);
                if (isAIVehicleRecommendationEnabled && isPrimaryScope) {
                    // AC-8.3: on INIT_SUCCESS, suppress this dispatch when AVO-on-Change-Vehicle is on
                    // (lazy fetch model). TRIGGER_AI_REFRESH and SET_SOFT_PULL_CONSENT_PROVIDED (which
                    // share this block) are explicit/consent-driven triggers and stay unconditional.
                    const isSuppressedForInitSuccess = isInitSuccessAction && isAiVehicleRecommendationsChangeVehicleEnabled;

                    if (!isSuppressedForInitSuccess) {
                        store.dispatch(fetchVehicleRecommendations());
                    }
                }
            }

            break;
        }
        case PAYMENT_RECEIVED: {
            const currentOfferType = getCurrentOfferType(state);
            const isFinanceOffer = currentOfferType === FINANCE;

            const originalActionType = action.meta?.originalAction?.type;
            const relevantActionTypes = [
                // Selling Price
                UPDATED_OFFER_PRICE_IMMEDIATE,
                // Cash Down
                UPDATED_DOWN_PAYMENT_IMMEDIATE,
                // Term
                SELECTED_TERM,
                // Vehicle
                SWITCH_VEHICLE,
                // Trade-in
                TRADE_IN_PAYMENT_INFO_UPDATE,
                INCLUDE_TRADE_INS_CHANGED,
                // VPP (Vehicle Protection Products)
                PRODUCT_CHANGES_PAYMENT_CALL,
                // Accessories
                ADDED_MANUAL_ACCESSORIES,
                ADD_ACCESSORIES_SELECTIONS,
                ADD_CATALOG_ACCESSORIES,
                REMOVE_CATALOG_ACCESSORIES,
                // Vehicle Recommendations
                APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS
            ];

            // ZIP edit on the customer card is only a re-request trigger when enableBuyerStateFromZip is
            // on; other whitelist entries above are unaffected by that toggle.
            const isZipEditTrigger =
                originalActionType === offerReduxActionTypes.UPDATED_SHOPPER_CONTACT &&
                featureToggleSelectors.isEnableBuyerStateFromZip(state);
            const isWhitelisted = relevantActionTypes.includes(originalActionType) || isZipEditTrigger;
            // Scenario creation (Add New Scenario) applies payment to seed a new scope; that is not a
            // user deal edit, so it opts out of the stale/refresh treatment via meta.skipAiRefresh.
            const skipAiRefresh = action.meta?.originalAction?.meta?.skipAiRefresh === true;
            const isAIAffordabilityIndicatorEnabled = featureToggleSelectors.isAIAffordabilityIndicatorEnabled(state);

            if (isFinanceOffer && isWhitelisted && !skipAiRefresh && isAIAffordabilityIndicatorEnabled) {
                store.dispatch(showAIRefreshButton());
            }
            break;
        }
        case SCENARIOS_INGEST_COMPLETE: {
            const currentOfferType = getCurrentOfferType(state);
            const isFinanceOffer = currentOfferType === FINANCE;

            if (isFinanceOffer) {
                const isLenderComparisonMVEnabled = featureToggleSelectors.isLenderComparisonMVEnabled(state);

                const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state as any);
                const scopeKeys = scopedStateSelectors.getActiveScopesOrder(state as any);

                // Only fetch AI data for non-primary scenarios
                for (const scopeKey of scopeKeys) {
                    // Skip a malformed/undefined scope key rather than crashing on .replace.
                    if (!scopeKey) {
                        continue;
                    }
                    const scopeId = scopeKey.replace('scopeId_', '');
                    if (scopeId === primaryScopeId) {
                        continue;
                    }

                    if (isLenderComparisonMVEnabled) {
                        store.dispatch(fetchLenderProbabilityData(scopeId, scopeId));
                    }
                }
            }

            break;
        }
        case offerReduxActionTypes.UPDATED_MANUAL_CREDIT_SCORE: {
            // Soft pull is the authoritative source — manual credit changes are no-ops while it's active
            if (getLenderCreditSource(state) === LENDER_CREDIT_SOURCE_SOFT_PULL) {
                break;
            }
            const defaultTier = getDealerDefaultCreditTier(state);
            const manualScore = action.payload as number;
            const isInDefaultRange =
                defaultTier?.score && manualScore >= defaultTier.score.low && manualScore <= defaultTier.score.high;
            const newSource = toCreditScoreSource(!!isInDefaultRange);
            if (getCreditScoreSource(state) !== newSource) {
                store.dispatch(offerReduxActionCreators.updateCreditScoreSource(newSource));
            }
            store.dispatch(triggerAIRefresh());
            break;
        }
        case offerReduxActionTypes.SELECTED_CREDIT_TIER: {
            // Soft pull is the authoritative source — tier changes are no-ops while it's active
            if (getLenderCreditSource(state) === LENDER_CREDIT_SOURCE_SOFT_PULL) {
                break;
            }
            const tier = getCreditTierById(state, action.payload as string);
            const newSource = toCreditScoreSource(!!tier.isDefault);
            if (getCreditScoreSource(state) !== newSource) {
                store.dispatch(offerReduxActionCreators.updateCreditScoreSource(newSource));
            }
            store.dispatch(triggerAIRefresh());
            break;
        }
        case AI_LENDER_PROBABILITY_SUCCESS: {
            const qualificationIds = formatQualificationIds(action.payload?.qualificationIds, state);

            if (qualificationIds.length > 0) {
                store.dispatch(updateQualificationIds(qualificationIds));
            }
            break;
        }
        default:
            break;
    }
};
